import { queryOptions } from '@tanstack/react-query'
import type { AuditFinding } from './auditFindings'
import { apiGet } from './client'

/**
 * `settings/Audit::snapshot`, peste `AuditSnapshotController` - o fotografie
 * datata, inghetata. Scrisul de mana e in `auditFindings.ts` (`writeRun`),
 * semnatura in `auditReport.ts` (`signRun`), cererea raportului in
 * `audit.ts` (`auditNext`).
 */
export type SnapshotRated = {
  rating: number | null
  colour: string
  automated: number
  manual: number
  weights: { auto: number; manual: number }
  gap?: string | number
}

export type SnapshotTick = { at: string; rating: number; colour: string }

export type SnapshotChapter = {
  ID: number
  ord: number
  title: string
  area_code: string
  side: string
  status: string
  finished_at: string
  findings_note: string
  /** Analiza, randata; goala cand capitolul nu e gata. */
  html: string
  /** Nota asa cum a fost socotita atunci, nu recalculata. */
  rated: SnapshotRated | null
  /** Nota ariei in timp, pentru aceeasi parte si firma. */
  history: SnapshotTick[]
  drift: string
  /** Raspunsul dinainte la aceeasi intrebare, de pus alaturi. */
  before: { ID: number; finished_at: string; html: string } | null
}

export type SnapshotRun = {
  ID: number
  type_id: number
  status: string
  /** De ce s-a oprit sau a picat; gol altfel. */
  error: string
  taken: string
  scope: string
  probe_set: string
  brief_set: string
  conversation_id: number
  signed_at: string | null
  report: string
  solutions: string
  report_at: string
  solutions_at: string
  /** Exista ciorna agentului, de pus la loc? */
  report_ai: boolean
  solutions_ai: boolean
}

export type SnapshotEvidence = { area: string; title: string; verdict: string; figure?: string | number | null }

export type SnapshotProbe = { device_name: string; area_code: string; probe_key: string; command_text: string; status: string }

export type AuditSnapshot = {
  version: string
  run: SnapshotRun
  type: { id: number; title: string } | null
  chapters: SnapshotChapter[]
  /** Pasi inca neterminati - pagina se reasculta cat sunt. */
  pending: number
  html: { report: string; solutions: string }
  hand: { report: string; solutions: string }
  evidence: SnapshotEvidence[]
  probes: SnapshotProbe[]
  findings: AuditFinding[]
  seal: boolean | null
  signed: boolean | null
  mayRun: boolean
  maySign: boolean
  review: { title: string; why: string }[]
  cost: { tokens: number; minutes: number }
}

export function auditSnapshotQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'audit', 'snapshot', id],
    queryFn: () => apiGet<AuditSnapshot>(`/settings/audit/snapshot/${id}`),
    /* Un raport sau solutiile pot fi inca in scris cand pagina se deschide
       din nou: cat sunt pasi neterminati se reasculta, ca `watch()` din
       audit.js. */
    refetchInterval: (query) => ((query.state.data?.pending ?? 0) > 0 ? 15_000 : false),
  })
}
